import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import { useSearchParams } from "react-router-dom";
import useCouplesPack from "@/features/couples-pack/hooks/use-couples-pack";
import { CouplesPackCard } from "@/features/couples-pack/ui/couples-pack-card";
import { SearchBar } from "@/components/common/search-bar";
import { CouplesPackFilters } from "@/features/couples-pack/ui/couples-pack-filters";
import { XIcon } from "lucide-react";
import { ContentLayout } from "@/components/common/content-layout";
import { UserDetailsDrawer } from "@/features/users/ui/user-details-drawer";
import { useState } from "react";

export default function CouplesPack() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);
  const page = parseInt(searchParams.get("page") || "0", 10);
  const usage_id = searchParams.get("usage_id") ?? undefined;

  // Build filter string from URL params
  const search = searchParams.get("search") || "";
  const professional_id = searchParams.get("professional_id") || undefined;
  const filterParts: string[] = [];
  const startDate = searchParams.get("start_date");
  const endDate = searchParams.get("end_date");
  const status = searchParams.get("status");

  if (startDate) filterParts.push(`start_date::${startDate}`);
  if (endDate) filterParts.push(`end_date::${endDate}`);
  if (status && status !== "all") filterParts.push(`status::${status}`);

  const filter = filterParts.join(",");

  const { data, error, isFetching, isLoading } = useCouplesPack(
    page,
    search,
    filter,
    professional_id
  );

  const couplesPacks = data?.couplesPacks || [];
  const currentPage = data?.currentPage || 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < lastPage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", newPage.toString());
      if (usage_id) newParams.set("usage_id", usage_id);
      setSearchParams(newParams);
    }
  };

  return (
    <ContentLayout
      header={
        <div className="flex flex-wrap items-center gap-4">
          <CouplesPackFilters usage_id={usage_id} />
          <div className="flex-1 min-w-[200px]">
            <SearchBar
              placeholder="Search Couples Packs..."
              urlParamStrategy="prefix"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />
              }}
              // fieldSelector={{
              //   enabled: true,
              //   fields: [
              //     { value: "name", label: "Name" },
              //     { value: "email", label: "Email" },
              //     { value: "phone", label: "Phone" }
              //   ],
              //   allowAll: true
              // }}
            />
          </div>
        </div>
      }
      data={couplesPacks}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load couples packs"
      emptyState={{
        message: "No couples packs found"
      }}
    >
      {couplesPacks.map((couplesPack: any) => (
        <CouplesPackCard
          key={couplesPack.id}
          couplesPack={couplesPack}
          onUserClick={(userId) => {
            setSelectedUserId(userId);
            setIsUserDrawerOpen(true);
          }}
        />
      ))}
      <UserDetailsDrawer
        userId={selectedUserId}
        isOpen={isUserDrawerOpen}
        onOpenChange={setIsUserDrawerOpen}
      />
      {couplesPacks.length > 0 && (
        <CustomPaginationComponent
          currentPage={currentPage}
          lastPage={lastPage}
          onPageChange={handlePageChange}
        />
      )}
    </ContentLayout>
  );
}
